import clsx from 'clsx';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import { DEFAULT_ICON_SIZE, Icon } from '@gitbook/spine-icons';
import { Emoji, type IconDefinition, IconPickerButton } from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { ContentIcon } from '../Icons';

export interface PageIconEditableRef {
    open: () => void;
}

const DEFAULT_PAGE_ICON_SIZE = 24;

/**
 * Icon or emoji for a page.
 */
export function PageIconEditable(props: {
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    page: hiveCore.RevisionPage;
    readOnly: boolean;
    size?: number;
    className?: string;
}): React.ReactElement | null {
    const {
        analyticsContext,
        page,
        size = DEFAULT_PAGE_ICON_SIZE,
        readOnly = true,
        className,
    } = props;
    const hiveBranch = useHiveBranch();

    const onChange = (update: hiveCore.RevisionPageIconProps) => {
        assertIsDefined(hiveBranch);

        if (update.emoji === page.emoji && update.icon === page.icon) {
            return;
        }

        hiveBranch.change((change) => {
            change.setPageIcon(page.id, update);
        });

        if (update.emoji) {
            analytics.logEvent({
                ...analyticsContext,
                type: 'edit_pick_page_emoji',
                pageKind: page.type,
                emoji: update.emoji,
            });
        } else if (update.icon) {
            analytics.logEvent({
                ...analyticsContext,
                type: 'edit_pick_page_icon',
                pageKind: page.type,
                icon: update.icon,
            });
        } else if (page.emoji) {
            analytics.logEvent({
                ...analyticsContext,
                type: 'edit_remove_page_emoji',
                pageKind: page.type,
            });
        } else {
            analytics.logEvent({
                ...analyticsContext,
                type: 'edit_remove_page_icon',
                pageKind: page.type,
            });
        }
    };

    if (readOnly) {
        return <PageIcon page={page} size={size} className="m-2" />;
    }

    return (
        <IconPickerButton
            value={page.icon || page.emoji}
            onPick={onChange}
            onRemove={() => onChange({})}
            renderIcon={(iconProps) => <ContentIcon {...iconProps} />}
            label={page.emoji || page.icon ? 'Edit icon' : 'Add icon'}
            size="xlarge"
            kind="blank"
            className={className}
        />
    );
}

/**
 * Read-only display of the icon or emoji for a page.
 */
export function PageIcon(props: {
    page: hiveCore.RevisionPageIconProps;
    size?: number;
    className?: string;
    fallback?: React.ReactNode;
}) {
    const { page, size, className } = props;
    const { fallback = <Icon.Page size={size} className={clsx('text-neutral-low', className)} /> } =
        props;

    if (page.icon) {
        return (
            <ContentIcon
                // @ts-ignore - icon is a IconName
                icon={page.icon}
                size={size}
                className={clsx(!size ? 'size-[1em]' : undefined, className)}
            />
        );
    }

    if (page.emoji) {
        return <Emoji code={page.emoji} size={size} className={className} />;
    }

    return fallback;
}

/**
 * Get an icon for a page, to be used in a palette.
 */
export function getPalettePageIcon(
    page: api.RevisionPageBase | hiveCore.RevisionPageIconProps,
    fallback = true
): IconDefinition | null {
    if (page.emoji || page.icon) {
        return <PageIcon page={page} size={DEFAULT_ICON_SIZE} />;
    }
    return fallback ? Icon.Page : null;
}
